import { WalletOverviewCreditInfoDialog } from 'app/src/features/UserAccount/components/WalletOverviewCreditInfoDialog'
import type { IconsCheckpoint } from 'app/src/model'
import { membershipApi, shoppingApi } from 'app/src/services/welfareportal'
import { FringeBenefitSpinner } from 'app/src/shared/components'
import { ChartIcon, Diamond, GoldBar, PiggyBank } from 'app/src/shared/components/Icons'
import { euroFormatter, getFromSessionStorage, getFunnelDiscount } from 'app/src/shared/helpers'
import { useWebAppContext } from 'app/src/shared/hooks'
import { Button } from 'primereact/button'
import { ProgressBar } from 'primereact/progressbar'
import { Tooltip } from 'primereact/tooltip'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const buildCheckpointsIcons = (checkpoints: number[] | undefined, credit: shoppingApi.CreditGetResult) => {
  if (!credit?.creditByServiceCategory?.welfareArt100?.total || credit.creditByServiceCategory.welfareArt100.total < 0) return []
  else
    return (
      checkpoints
        ?.sort((a, b) => a - b)
        .slice(0, 3)
        .map((checkpoint, index) => {
          const icon: IconsCheckpoint = {
            checkpoint,
            icon:
              index == 0
                ? (par) => <PiggyBank {...par} />
                : index == 1
                  ? (par) => <GoldBar {...par} />
                  : index == 2
                    ? (par) => <Diamond {...par} />
                    : () => <></>,
          }

          return icon
        }) ?? []
    )
}

export function WalletOverviewSection() {
  const context = useWebAppContext()
  const funnelDiscount = getFunnelDiscount(context)
  const campaignStorage = getFromSessionStorage('selectedCampaign') ?? '{}'
  const membershipId = JSON.parse(campaignStorage).membershipId

  const credit = shoppingApi.useCreditGet(
    {},
    {
      enabled: !funnelDiscount.onlyDiscount,
      meta: {
        skip404Errors: true,
      },
    },
  )
  const [showDialog, setShowDialog] = useState(false)
  const membership = membershipApi.useMembershipGet({ pathParams: { membershipId: membershipId } })
  const { t } = useTranslation('userAccount', { keyPrefix: 'WalletOverview' })

  const total = useMemo(() => credit?.data?.creditByServiceCategory.welfareArt100.total ?? 0, [credit?.data])
  const available = useMemo(() => credit?.data?.creditByServiceCategory.welfareArt100.available, [credit?.data])
  const expenses = useMemo(() => {
    if (total && available) {
      return total - available
    } else return 0
  }, [total, available])

  const spendingReminderConfig = context.userInfo.welfareCampaign?.configuration?.spendingReminder
  const thresholdDays = context.userInfo.welfareCampaign?.configuration?.spendingReminder?.thresholdDays

  const shouldShowSpendingReminder = useMemo(() => {
    if (!spendingReminderConfig || !thresholdDays) return false

    const campaignEndDate = context.userInfo.welfareCampaign?.fruitionDateTo
    if (!campaignEndDate) return false

    const today = new Date()
    const endDate = new Date(campaignEndDate)
    const diffTime = endDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    const threshold = typeof thresholdDays === 'string' ? parseInt(thresholdDays, 10) : thresholdDays

    return diffDays <= threshold && diffDays >= 0
  }, [spendingReminderConfig, thresholdDays, context.userInfo.welfareCampaign?.fruitionDateTo])

  const showCheckpoints = useMemo(() => {
    if (!membership.isError && membership?.data && membership?.data.membershipInfo.welfarePercentage > 0) {
      // check if activeMembership.data.companyPrize type is mixed between percentage and amount
      if (membership.data.membershipInfo.companyPrize && membership.data.membershipInfo.companyPrize.length > 0) {
        // iterate on each companyPrize and populate a map with count of each prizeType
        const prizeTypeCount: Record<string, number> = {}
        membership.data.membershipInfo.companyPrize.forEach((companyPrize) => {
          companyPrize.prizes.forEach((prize) => {
            if (prize.prizeType in prizeTypeCount) {
              prizeTypeCount[prize.prizeType] += 1
            } else {
              prizeTypeCount[prize.prizeType] = 1
            }
          })
        })

        // check if there are both 'percentage' and 'amount' in rangeTypeCount
        const hasMixedPrizeTypes = Object.keys(prizeTypeCount).length > 1

        return !hasMixedPrizeTypes
      }
    }
    return false
  }, [membership.data, membership.isError])

  const usedPercentage = useMemo(() => (expenses > 0 && total > 0 ? (expenses / total) * 100 : 0), [expenses, total])

  const prizesData = useMemo(() => {
    if (
      !credit.isError &&
      !membership.isError &&
      credit?.data &&
      membership?.data &&
      membership?.data.membershipInfo.welfarePercentage > 0 &&
      total > 0
    ) {
      const pdrAssigned = credit.data.pdrAssigned

      const selectedPercentage = membership.data.membershipInfo.welfarePercentage

      //Check Prize Group Speculari
      let prizeGroupCheckOk = true
      membership?.data?.membershipInfo?.companyPrize?.sort((companyPrizeA, companyPrizeB) => {
        if (companyPrizeA.prizes?.length != companyPrizeB.prizes?.length) {
          prizeGroupCheckOk = false
          return -1
        }
        companyPrizeA.prizes.forEach((companyPrizeAElement, companyPrizeElementIndex) => {
          const companyPrizeBElement = companyPrizeB.prizes[companyPrizeElementIndex]
          if (
            companyPrizeAElement.range.from !== companyPrizeBElement.range.from ||
            companyPrizeAElement.range.fromInclusive !== companyPrizeBElement.range.fromInclusive ||
            companyPrizeAElement.range.to !== companyPrizeBElement.range.to ||
            companyPrizeAElement.range.toInclusive !== companyPrizeBElement.range.toInclusive
          ) {
            prizeGroupCheckOk = false
            return
          }
        })

        return 0
      })
      let calcCheckpoints: number[] = []
      let prizes: number[] = []
      let prizesInitDone = false

      if (prizeGroupCheckOk) {
        membership.data.membershipInfo.companyPrize?.forEach((companyPrize, companyPrizeIndex) => {
          if (!prizesInitDone) prizes = new Array(companyPrize.prizes.length).fill(0)
          if (!prizesInitDone) calcCheckpoints = new Array(companyPrize.prizes.length).fill(0)
          prizesInitDone = true
          companyPrize.prizes.forEach((currentPrize, currentPrizeIndex) => {
            if (
              (currentPrize.rangeType === 'percentage' &&
                (selectedPercentage > currentPrize.range?.from ||
                  (currentPrize.range?.fromInclusive && currentPrize.range.from === selectedPercentage))) ||
              (currentPrize.rangeType === 'amount' &&
                (pdrAssigned > currentPrize.range?.from ||
                  (currentPrize.range?.fromInclusive && currentPrize.range.from === pdrAssigned)))
            ) {
              if (currentPrize.prizeType === 'percentage') {
                prizes[currentPrizeIndex] += currentPrize.prize * pdrAssigned
              } else {
                prizes[currentPrizeIndex] += currentPrize.prize
              }

              if (companyPrizeIndex == 0) {
                //checkpoint: PDR * (Min Percentage Prize Group)
                if (currentPrize.rangeType === 'percentage') {
                  calcCheckpoints[currentPrizeIndex] =
                    (currentPrize.range.from + (currentPrize.range.fromInclusive ? 0 : 0.1)) * pdrAssigned
                } else {
                  calcCheckpoints[currentPrizeIndex] = currentPrize.range.from + (currentPrize.range.fromInclusive ? 0 : 0.01)
                }
              }
            } else {
              prizes[currentPrizeIndex] = -1
              calcCheckpoints[currentPrizeIndex] = -1
            }
          })
        })
        calcCheckpoints = calcCheckpoints.filter((x) => x >= 0)
        prizes = prizes.filter((x) => x >= 0)

        let partial = 0
        for (let i = 0; i < calcCheckpoints.length; i++) {
          {
            partial = 0
            if (i > 0) {
              partial += prizes[i - 1]
            }
            calcCheckpoints[i] = calcCheckpoints[i] + partial

            calcCheckpoints[i] = parseFloat(calcCheckpoints[i].toFixed(2))
          }
        }
      }

      const originalPrizes = prizes?.slice(0) ?? []

      let usedBonus = 0
      if (expenses > calcCheckpoints[0]) {
        let delta: number = 0
        const tempPrizes = originalPrizes?.slice(0)

        for (let i: number = 0; i < calcCheckpoints.length; i++) {
          delta = expenses - calcCheckpoints[i]
          if (delta <= 0) break
          else {
            if (delta >= tempPrizes[i]) {
              usedBonus += tempPrizes[i]
              if (i != calcCheckpoints.length - 1)
                for (let j = i + 1; j < calcCheckpoints.length; j++) {
                  tempPrizes[j] -= tempPrizes[i]
                }
            } else {
              usedBonus += delta
              break
            }
          }
        }
      }
      usedBonus = parseFloat(usedBonus.toFixed(2))
      return {
        originalPrizes,
        checkpoints: calcCheckpoints ?? [],
        usedBonus,
      }
    }
  }, [membership.data, membership.isError, credit.data, credit.isError, expenses, total])

  return !credit.isError && credit.data && total > 0 && available && available > 0 && !funnelDiscount.onlyDiscount ? (
    <div
      className='depth-8 card grid grid-cols-1 divide-x-0 divide-y-2 rounded-xl border border-solid border-[var(--primary-color)] p-5 lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'
      data-testid='WalletOverviewSectionProgressBar'
    >
      <div
        className={`col-span-12 p-2 ${credit?.data?.creditByServiceCategory?.welfareArt51?.total !== 0 ? 'lg:col-span-8' : ''}`}
      >
        {available && available > 0 ? (
          <span className='text-xl font-semibold'>
            {t('AvailableCreditText', {
              AVAILABLECREDIT: euroFormatter.format(available),
            })}
          </span>
        ) : (
          <></>
        )}
        <div className='mt-5'>
          <div className='flex justify-between text-base font-bold'>
            <span>{euroFormatter.format(0)}</span>
            <div className='flex items-center gap-2'>
              <span>{euroFormatter.format(total)}</span>
              {credit.data?.info && credit.data.info.includes('note') && (
                <Button
                  icon='pi pi-info-circle text-2xl'
                  onClick={() => setShowDialog(true)}
                  size='small'
                  name='creditInfo'
                  aria-label='Informazioni credito'
                  pt={{ root: { className: 'w-8 h-8 bg-white text-black border-white' } }}
                />
              )}
            </div>
          </div>
          <div className='mt-4'>
            <Tooltip target='.tooltip-checkpoint' pt={{ text: { className: 'bg-[var(--card-background)] text-black' } }} />

            <div className='h-[60px] w-full'>
              <ProgressBar
                value={usedPercentage}
                aria-label={`Progresso utilizzo credito`}
                pt={{ label: { className: 'hidden' }, root: { className: 'mt-2' } }}
              />
              {showCheckpoints &&
                buildCheckpointsIcons(prizesData?.checkpoints, credit?.data)?.map(
                  (checkpointItem: IconsCheckpoint, xindex: number) => {
                    const relativeCheckpoint = (checkpointItem.checkpoint * 100) / total
                    const disabled = total > 0 ? usedPercentage < relativeCheckpoint : true

                    return (
                      <div
                        className='z-3 tooltip-checkpoint relative w-[52px] cursor-help'
                        data-pr-tooltip={t('Checkpoint', {
                          CHECKPOINTINDEX: `#${xindex + 1}`,
                          CHECKPOINTAMOUNT: euroFormatter.format(checkpointItem.checkpoint ?? 0),
                        })}
                        data-pr-position='bottom'
                        key={`icon_${xindex}`}
                        style={{
                          left: `calc(${relativeCheckpoint}% - 20px)`,
                          top: `calc(-37px - ${xindex * 52}px)`,
                        }}
                      >
                        <div className='flex h-[52px] w-[52px] items-center justify-center rounded-[50px] border border-solid bg-[white]'>
                          {checkpointItem.icon({
                            width: 36,
                            fill: disabled ? '#AEB5BC' : 'var(--primary-color)',
                            className: 'bg-[white] mx-auto',
                          })}
                        </div>
                      </div>
                    )
                  },
                )}
            </div>
            <div>
              <span className='mx-auto flex flex-col text-center'>
                {t('UsedUntilNow', { USEDCREDIT: euroFormatter.format(expenses) })}
              </span>
            </div>
          </div>
        </div>
        {showCheckpoints &&
          prizesData?.originalPrizes &&
          prizesData?.originalPrizes.length > 0 &&
          prizesData?.originalPrizes[prizesData?.originalPrizes.length - 1] > 0 && (
            <div className='mt-3 bg-[var(--card-background)] p-4'>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('AdditionalCreditUsedText', {
                    ADDITIONALUSEDCREDIT: euroFormatter.format(prizesData?.usedBonus ?? 0),
                    TOTALADDITIONALCREDIT: euroFormatter.format(
                      prizesData?.originalPrizes[prizesData?.originalPrizes.length - 1],
                    ),
                  }),
                }}
              />
              {shouldShowSpendingReminder && (
                <div className='mb-6 mt-6 flex items-center rounded-md bg-[var(--semantic-warning-light)] p-3 text-sm'>
                  <span className='mr-3'>
                    <i className={`pi pi-exclamation-circle h-[20px] w-[20px] content-center text-2xl text-[#995C00]`} />
                  </span>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('AdditionalCreditSpendingReminderText', {
                        ADDITIONALAVAILABLECREDIT: euroFormatter.format(
                          (prizesData?.originalPrizes[prizesData?.originalPrizes.length - 1] ?? 0) - (prizesData?.usedBonus ?? 0),
                        ),
                      }),
                    }}
                  />
                </div>
              )}

              <div className='mt-3 flex flex-row flex-wrap items-center justify-start gap-10'>
                {buildCheckpointsIcons(prizesData?.checkpoints, credit?.data)?.map(
                  (checkpointItem: IconsCheckpoint, xindex: number) => {
                    const relativeCheckpoint = (checkpointItem.checkpoint * 100) / total

                    const disabled = usedPercentage < relativeCheckpoint
                    return (
                      <div key={`chicon_${xindex}`}>
                        <ChartIcon
                          className='relative flex h-[52px] w-[52px] cursor-help items-center justify-center rounded-[50px] border border-solid bg-[white]'
                          withLocker={disabled}
                          backgroundColor={disabled ? 'white ' : 'var(--primary-color)'}
                          borderColor={disabled ? '#eaeaea' : 'white'}
                          ariaLabel={t(disabled ? 'CheckpointLockedAriaLabel' : 'CheckpointUnlockedAriaLabel', {
                            CHECKPOINTINDEX: `#${xindex + 1}`,
                            CHECKPOINTAMOUNT: euroFormatter.format(checkpointItem.checkpoint ?? 0),
                          })}
                        >
                          <div
                            className='tooltip-checkpoint'
                            data-pr-tooltip={t('Checkpoint', {
                              CHECKPOINTINDEX: `#${xindex + 1}`,
                              CHECKPOINTAMOUNT: euroFormatter.format(checkpointItem.checkpoint ?? 0),
                            })}
                            data-pr-position='bottom'
                          >
                            {checkpointItem.icon({ width: 36, fill: 'var(--card-background)' })}
                          </div>
                        </ChartIcon>
                        <div className={`flex flex-row items-center justify-start`} style={{ margin: '10px 0' }}>
                          {[...Array(xindex + 1)].map((_, index) => {
                            let value: number
                            if (disabled) value = 0
                            else {
                              if (
                                index < xindex ||
                                (prizesData?.checkpoints[xindex + 1] && expenses > prizesData?.checkpoints[xindex + 1])
                              )
                                value = 100
                              else {
                                const upperBound = prizesData?.checkpoints[xindex + 1]
                                  ? prizesData?.checkpoints[xindex + 1]
                                  : (total ?? 0)

                                value =
                                  upperBound && prizesData?.checkpoints[xindex]
                                    ? (expenses - prizesData?.checkpoints[xindex]) /
                                      (upperBound - prizesData?.checkpoints[xindex])
                                    : 0

                                value *= 100
                              }
                            }
                            return (
                              <ProgressBar
                                aria-label={`Progresso utilizzo credito per il checkpoint #${xindex + 1}`}
                                value={value}
                                key={`hl_${xindex}_${index}`}
                                pt={{ label: { className: 'hidden' }, root: { className: 'checkpointrect rounded-none' } }}
                              />
                            )
                          })}
                        </div>
                        <div className='font-bold'>
                          {t('Checkpoint', {
                            CHECKPOINTINDEX: `#${xindex + 1}`,
                            CHECKPOINTAMOUNT: '',
                          })}
                        </div>
                      </div>
                    )
                  },
                )}
              </div>
            </div>
          )}
      </div>
      {credit?.data?.creditByServiceCategory?.welfareArt51?.total !== 0 && (
        <div className='col-span-12 mx-auto w-full p-2 lg:col-span-4'>
          <FringeBenefitSpinner />
        </div>
      )}

      {showDialog && <WalletOverviewCreditInfoDialog onHide={() => setShowDialog(false)} />}
    </div>
  ) : (
    <div data-testid='WalletOverviewSectionEmpty'></div>
  )
}
